import type { ValidateGraphResponse } from '@/types/custom-playbooks';
import type {
  AgentDto,
  ConnectionType,
  PatchAgentBody,
  SettingsIssue,
  ToolKey,
} from '@/types/gtm-agents';
import {
  abilityChange,
  abilityOn,
  addConnection,
  changeConnection,
  isConnectionType,
  isToolKey,
  toolLabel,
} from './agent-tab.util';
import { CONNECTION_NAME } from './library.util';

/**
 * What the agent's settings do to its plays is the API's one decision
 * (cleanse-api `providers/agents/tool_policy.py`): a step that needs an
 * ability that is off, an app removed from the agent, a tool set to Never or
 * an acting user who left keeps the play from running, and a write set to Ask
 * each time holds its records for the acting user first. Every surface asks it
 * and shows its words: the agent carries the answer (`blocked`), and the
 * play's validate route gives it step by step. This file only reads the
 * answer, and builds the one click that puts a setting back.
 */

/** The validate route's codes for a step the agent's settings keep from running. */
const BLOCKING_CODES: ReadonlySet<string> = new Set([
  'agent_ability_off',
  'agent_connection_off',
  'agent_policy_never',
  'agent_acting_user_gone',
]);

/** The validate route's warning for a step that asks someone first. */
export const STEP_ASKS_CODE = 'agent_step_asks';

/** An issue that keeps a step from running. Not every `agent_` code is one. */
export function isSettingsError(issue: { code: string }): boolean {
  return BLOCKING_CODES.has(issue.code);
}

/** Any issue the agent's settings raise on a step: an error, or "Asks Jane first". */
export function isSettingsIssue(issue: { code: string }): boolean {
  return isSettingsError(issue) || issue.code === STEP_ASKS_CODE;
}

// ---------------------------------------------------------------------------
// On the agent
// ---------------------------------------------------------------------------

/**
 * What keeps the agent's plays from running, first one first; one play's when
 * `playId` is given. Empty from an API that predates the check.
 */
export function blockedSteps(
  agent: Pick<AgentDto, 'blocked'>,
  playId?: string
): SettingsIssue[] {
  const blocked = agent.blocked ?? [];
  return playId === undefined
    ? blocked
    : blocked.filter((issue) => issue.playId === playId);
}

/** The abilities the agent's plays use. */
export function abilitiesInUse(
  agent: Pick<AgentDto, 'abilitiesInUse'>
): ReadonlySet<ToolKey> {
  return new Set(agent.abilitiesInUse ?? []);
}

/**
 * What keeps one tool of one app from running now, where no step is asked
 * about (a switch on a step's panel): the API's answer on the agent
 * (`appBlocks`), in the validate route's codes. The route says it for a step.
 */
export function appBlock(
  agent: Pick<AgentDto, 'appBlocks'>,
  type: ConnectionType,
  tool: ToolKey
): string | undefined {
  return agent.appBlocks?.[type]?.[tool];
}

// ---------------------------------------------------------------------------
// On the canvas
// ---------------------------------------------------------------------------

/** One click that puts a setting back, built from the agent as it stands. */
export interface AgentFix {
  label: string;
  change: (current: AgentDto) => PatchAgentBody;
}

/** What an issue names; the one click is built from it, never from its words. */
interface IssueFields {
  code: string;
  key?: string | null;
  connection?: string | null;
}

/**
 * The click that clears an issue: turn the ability back on, add the app back,
 * Always allow the tool, or act as the person looking at the page. None when
 * the issue names nothing to change (or no one is looking).
 */
export function settingsFix(
  issue: IssueFields,
  currentUserId: string | null | undefined
): AgentFix | undefined {
  const key = isToolKey(issue.key) ? issue.key : undefined;
  const connection = isConnectionType(issue.connection)
    ? issue.connection
    : undefined;
  switch (issue.code) {
    case 'agent_ability_off':
      return key
        ? {
            label: 'Turn on ' + toolLabel(key),
            change: (current) => abilityChange(current, key, true),
          }
        : undefined;
    case 'agent_connection_off':
      return connection
        ? {
            label: 'Add ' + CONNECTION_NAME[connection],
            // As it was: whoever it acted as stays.
            change: (current) => addConnection(current, connection, null),
          }
        : undefined;
    case 'agent_policy_never':
      return key && connection
        ? {
            label: 'Always allow',
            change: (current) =>
              changeConnection(current, connection, {
                policy: { [key]: 'allow' },
              }),
          }
        : undefined;
    case 'agent_acting_user_gone':
      return connection && currentUserId
        ? {
            label: 'Act as me',
            change: (current) =>
              changeConnection(current, connection, {
                actingUserId: currentUserId,
              }),
          }
        : undefined;
    default:
      return undefined;
  }
}

/** A step the agent's settings keep from running, with its click. */
export interface AgentStepIssue {
  nodeId: string;
  code: string;
  message: string;
  fix?: AgentFix;
}

export interface AgentStepChecks {
  /** Each blocked step's issues: what its agent has off, with the fix. */
  blocks: ReadonlyMap<string, AgentStepIssue[]>;
  /** "Asks Jane first" on a write step set to Ask each time. */
  asks: ReadonlyMap<string, string>;
}

const NO_CHECKS: AgentStepChecks = { blocks: new Map(), asks: new Map() };

/**
 * The agent's settings on each step of the play on the canvas, read off the
 * validate route's answer: an error per blocked step, and a warning for a step
 * that asks first. Nothing before the first answer. `turnsOn` is what a play
 * proposal under review turns on when it is accepted: the route answers about
 * the agent as it stands, so those abilities don't read as off.
 */
export function agentStepChecks(
  response:
    | Partial<Pick<ValidateGraphResponse, 'errors' | 'warnings'>>
    | undefined,
  currentUserId: string | null | undefined,
  turnsOn: readonly string[] = []
): AgentStepChecks {
  if (!response) return NO_CHECKS;
  const blocks = new Map<string, AgentStepIssue[]>();
  for (const issue of response.errors ?? []) {
    if (!issue.nodeId || !isSettingsError(issue)) continue;
    if (
      issue.code === 'agent_ability_off' &&
      issue.key &&
      turnsOn.includes(issue.key)
    )
      continue;
    blocks.set(issue.nodeId, [
      ...(blocks.get(issue.nodeId) ?? []),
      {
        nodeId: issue.nodeId,
        code: issue.code,
        message: issue.message,
        fix: settingsFix(issue, currentUserId),
      },
    ]);
  }
  const asks = new Map<string, string>();
  for (const issue of response.warnings ?? [])
    if (issue.nodeId && issue.code === STEP_ASKS_CODE)
      asks.set(issue.nodeId, issue.message);
  return blocks.size || asks.size ? { blocks, asks } : NO_CHECKS;
}

/**
 * What accepting a play proposal turns on: the abilities its `agentChanges`
 * switch on. Clu can switch one off by itself, never on, so a proposal only
 * ever carries these.
 */
export function abilitiesTurnedOn(
  changes: { tools?: Partial<Record<ToolKey, boolean>> } | undefined | null
): ToolKey[] {
  return Object.entries(changes?.tools ?? {})
    .filter(([key, on]) => on === true && isToolKey(key))
    .map(([key]) => key as ToolKey);
}

/**
 * What the validate route says keeps one step from running: its first reason
 * and the server's words for it. Nothing before the route answers, and
 * nothing for a step that asks first (that is a state, not a block).
 */
export function nodeBlock(
  response:
    | Partial<Pick<ValidateGraphResponse, 'errors' | 'warnings'>>
    | undefined,
  nodeId: string
): { code: string; message: string } | undefined {
  const issue = response?.errors?.find(
    (item) => item.nodeId === nodeId && isSettingsError(item)
  );
  return issue ? { code: issue.code, message: issue.message } : undefined;
}

/**
 * A play proposal's "Turns on Enrich, Validate" line, or null. With the agent
 * at hand, an ability it already has on is left out.
 */
export function turnsOnLine(
  changes: { tools?: Partial<Record<ToolKey, boolean>> } | undefined | null,
  agent?: Parameters<typeof abilityOn>[0]
): string | null {
  const keys = abilitiesTurnedOn(changes)
    .filter((key) => !agent || !abilityOn(agent, key))
    .map(toolLabel);
  return keys.length ? 'Turns on ' + keys.join(', ') : null;
}
